import firebase from 'firebase/app';
import 'firebase/firestore';

import axios from 'axios';

import moment from 'moment';
import { formatCurrency } from '@qualle-admin/qutil/dist/currency';
import { getTimeDiff } from '@qualle-admin/qutil/dist/date';
import { uniqBy, partition } from 'lodash';
import Vue from 'vue';
import { validateSubscriptionPlan } from '@/validators/bookings';
import { getloadsRequestedLocations } from '@/service/location';

const state = {
  payment_methods: [],
  payout_methods: [],
  payments: [],
  payouts: [],
  payment_method_status: false, // Getting payment_method query excution status
  subscription: null,
  product_plans: [],
  invoice_data: null,
  subscription_invoices: [],
  transaction_invoices: [],
  requestload_invoices: [],
  pending_inovice_data: [],
};

const mutations = {
  set_payment_methods(state, payload) {
    state.payment_methods = payload;
  },
  set_payment_methods_status(state, payload) {
    state.payment_method_status = payload;
  },
  set_payout_methods(state, payload) {
    state.payout_methods = payload;
  },
  set_payments(state, payload) {
    state.payments = payload;
  },
  set_payouts(state, payload) {
    state.payouts = payload;
  },
  set_subscription(state, payload) {
    state.subscription = payload;
  },
  set_product_plans(state, payload) {
    state.product_plans = payload;
  },
  set_invoice_data(state, payload) {
    state.invoice_data = payload;
  },
  set_subscription_invoices(state, payload) {
    state.subscription_invoices = payload;
  },
  set_transaction_invoices(state, payload) {
    state.transaction_invoices = payload;
  },
  set_pending_invoice(state, payload) {
    state.pending_inovice_data = payload;
  },
  set_requestload_invoices(state, payload) {
    state.requestload_invoices = payload;
  },
  reset(state) {
    state.subscription = null;
    state.paymentMethods = [];
    state.payoutMethods = [];
    state.payments = [];
    state.payouts = [];
  },
};

const actions = {
  update_payment_methods: async ({ commit }, payload) => {
    commit('set_payment_methods', payload);
  },
  update_payment_methods_status: async ({ commit }, payload) => {
    commit('set_payment_methods_status', payload);
  },
  update_payout_methods: async ({ commit }, payload) => {
    commit('set_payout_methods', payload);
  },
  update_payments: async ({ commit }, payload) => {
    commit('set_payments', payload);
  },
  update_payouts: async ({ commit }, payload) => {
    commit('set_payouts', payload);
  },
  update_subscriptions: async ({ commit, rootGetters, dispatch }, payload) => {
    const data = payload.find(
      (val) => val.status !== 'canceled' && val.status !== 'incomplete_expired'
    );

    commit('set_subscription', data);
    if (
      validateSubscriptionPlan() &&
      !rootGetters['auth/get_customer']?.shipper_locations
    ) {
      const requestedLocations = getloadsRequestedLocations(
        rootGetters['bookings/get_trucker_commitments']
      );
      dispatch('update_shipper_locations', requestedLocations);
    }
  },
  set_default_payment_method: async ({ rootGetters }, payload) => {
    const { id: paymentMethodId } = payload;
    const orgId = rootGetters['auth/get_user'].orgId;

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(orgId)
      .get();
    const { owner_uid: ownerUid, agency_scacs: agencyScacs } =
      organizationRef.data();

    const customerRef = firebase
      .firestore()
      .collection('stripe_customers')
      .doc(ownerUid);

    const { docs: paymentMethods } = await customerRef
      .collection('payment_methods')
      .get();

    await Promise.all(
      paymentMethods.map(
        async (method) =>
          await method.ref.update({
            is_default: method.data().id === paymentMethodId,
          })
      )
    );

    if (agencyScacs) {
      const agencyCustomerId = rootGetters['auth/get_customer'].customer_id;
      const { docs: agentCustomerDocs } = await firebase
        .firestore()
        .collection('stripe_customers')
        .where('agency_customer_id', '==', agencyCustomerId)
        .get();
      agentCustomerDocs.map(async (customer) => {
        const agentPaymentMethodRef = await customer.ref.collection(
          'payment_methods'
        );

        const { docs: agentPaymentMethod } = await agentPaymentMethodRef
          .where('is_agency', '==', true)
          .get();

        if (agentPaymentMethod.length) {
          await agentPaymentMethod[0].ref.delete();
        }

        const defaultPaymentMethod = paymentMethods.find(
          (pm) => pm.data().id === paymentMethodId
        );
        const defaultPayment = defaultPaymentMethod.data();

        const { docs: paymentDocs } = await agentPaymentMethodRef.get();
        if (paymentDocs.length) {
          const defaultMethod = paymentDocs.find(
            (doc) => doc.data().is_default === true
          );
          if (!defaultMethod) {
            defaultPayment.is_default = true;
          }
        } else {
          defaultPayment.is_default = true;
        }
        defaultPayment.is_agency = true;

        await agentPaymentMethodRef.add(defaultPayment);
      });
    }
    const defaultPayment = paymentMethods.find(
      (val) => val.data().id === paymentMethodId
    );
    const defaultPaymentData = defaultPayment.data();
    if (!defaultPaymentData.is_agency) {
      return await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/accounts/update`,
        {
          orgId,
          payload: {
            invoice_settings: {
              default_payment_method: paymentMethodId,
            },
          },
        }
      );
    }
  },
  delete_payment_method: async ({ rootGetters }, payload) => {
    const { id: paymentMethodId } = payload;

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(rootGetters['auth/get_user'].orgId)
      .get();
    const { owner_uid: ownerUid } = organizationRef.data();

    const customerRef = firebase
      .firestore()
      .collection('stripe_customers')
      .doc(ownerUid);

    const { docs: paymentMethods } = await customerRef
      .collection('payment_methods')
      .where('id', '==', paymentMethodId)
      .get();

    const paymentMethodRef = paymentMethods[0].ref;

    await paymentMethodRef.delete();
  },
  delete_payout_method: async ({ rootGetters }, payload) => {
    const { id: payoutMethodId } = payload;

    const organizationRef = await firebase
      .firestore()
      .collection('organizations')
      .doc(rootGetters['auth/get_user'].orgId)
      .get();
    const { owner_uid: ownerUid } = organizationRef.data();

    const customerRef = firebase
      .firestore()
      .collection('stripe_customers')
      .doc(ownerUid);

    const { docs: payoutMethods } = await customerRef
      .collection('payout_methods')
      .where('id', '==', payoutMethodId)
      .get();

    const payoutMethodRef = payoutMethods[0].ref;

    await payoutMethodRef.delete();
  },
  create_subscription: async ({ commit, rootGetters, dispatch }, payload) => {
    const { paymentMethod, priceId, metadata } = payload;
    try {
      const subscription = await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/subscriptions/create`,
        {
          paymentMethod,
          priceId,
          metadata,
        }
      );

      if (subscription.data.status !== 'active') {
        dispatch(
          'auth/new_notification',
          {
            type: 'notification',
            error: true,
            message: 'Your subscription is incomplete please try again!',
          },
          { root: true }
        );
      }
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
  cancel_subscription: async ({ dispatch, rootGetters }, payload) => {
    const { subscriptionData, orgId } = payload;
    try {
      await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/subscriptions/cancel`,
        {
          subscriptionData,
          orgId,
        }
      );
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
  update_subscription: async ({ dispatch, rootGetters }, payload) => {
    const { paymentMethod, subscriptionId, priceId, id, customerId, metadata } =
      payload;
    try {
      const subscription = await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/subscriptions/update`,
        {
          paymentMethod,
          subscriptionId,
          customerId,
          priceId,
          id,
          orgId: rootGetters['auth/get_user'].orgId,
          turnsUsage: rootGetters['auth/get_customer'].turns_usage || 0,
          metadata,
        }
      );
      if (subscription.data.status !== 'active') {
        dispatch(
          'auth/new_notification',
          {
            type: 'notification',
            error: true,
            message: 'Your subscription is incomplete please try again!',
          },
          { root: true }
        );
      } else {
        const customerRef = firebase
          .firestore()
          .collection('stripe_customers')
          .doc(rootGetters['auth/get_org'].owner_uid);

        const { docs: subscriptions } = await customerRef
          .collection('subscriptions')
          .where('id', '==', subscriptionId)
          .get();

        const subscriptionRef = subscriptions[0].ref;

        await subscriptionRef.update(subscription.data);
      }
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
  get_product_plans: async ({ commit }) => {
    const { data } = await axios.get(
      `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/products/getAll`
    );
    commit('set_product_plans', data.reverse());
  },
  get_invoices: async ({ commit, rootGetters }, payload) => {
    const { data } = await axios.post(
      `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/invoices/GetAll?customerId=${rootGetters['auth/get_customer'].customer_id}`,
      { startAt: payload }
    );
    if (payload === 0) {
      commit('set_invoice_data', data);
    } else {
      return data;
    }
  },
  // calculating tax based on the postal code
  get_tax: async ({ commit, rootGetters, dispatch }, payload) => {
    try {
      const taxData = await axios.post(
        `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/tax/calculate_tax`,
        {
          amount: payload.amount,
          postal_code: payload.postal_code,
        }
      );
      return taxData.data;
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },

  get_subscription_invoices: async ({ commit, dispatch, rootGetters }) => {
    try {
      const customerRef = firebase
        .firestore()
        .collection('stripe_customers')
        .doc(`${rootGetters['auth/get_org'].owner_uid}`);

      Vue.prototype.$snapshots.$subscription_invoice_snapshot = customerRef
        .collection('invoices')
        .where('billing_reason', '!=', 'manual')
        .onSnapshot(({ docs }) => {
          const data = docs.map((val) => val.data());
          const previous =
            rootGetters['stripe/get_subscription_invoices_data'] || [];

          const allData = uniqBy([...data, ...previous], ({ id }) => id);
          commit('set_subscription_invoices', allData);
        });

      const customerData = (await customerRef.get()).data();
      let agencyCustomerId = null;
      const customerId = customerData?.customer_id;
      if (customerData?.agency_customer_id) {
        agencyCustomerId = customerData?.agency_customer_id;
      }
      // Getting subscription invoices to the agent requested using agency payment method
      if (agencyCustomerId) {
        const { docs } = await firebase
          .firestore()
          .collection('stripe_customers')
          .where('customer_id', '==', agencyCustomerId)
          .get();
        const agencyCustomerRef = docs[0].ref;
        Vue.prototype.$snapshots.$subscription_agency_invoice_snapshot =
          await agencyCustomerRef
            .collection('invoices')
            .where('subscription.metadata.customerId', '==', customerId)
            .onSnapshot(({ docs }) => {
              const paymentData = docs.map((val) => val.data());

              const previous =
                rootGetters['stripe/get_subscription_invoices_data'] || [];
              const allData = uniqBy(
                [...paymentData, ...previous],
                ({ id }) => id
              );

              commit('set_subscription_invoices', allData);
            });
      }
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
  get_transaction_invoices: async ({ commit, dispatch, rootGetters }) => {
    try {
      const customerRef = firebase
        .firestore()
        .collection('stripe_customers')
        .doc(`${rootGetters['auth/get_org'].owner_uid}`);

      Vue.prototype.$snapshots.$transaction_invoice_snapshot = customerRef
        .collection('invoices')
        .where('billing_reason', '==', 'manual')
        .orderBy('created', 'desc')
        .onSnapshot(({ docs }) => {
          const data = docs.map((val) => val.data());
          const transactions = partition(data, function(a) {
            return a.description?.includes('committed');
          });
          const previousLoads =
            rootGetters['stripe/get_requestload_invoices'] || [];
          const allLoadsData = uniqBy(
            [...transactions[0], ...previousLoads],
            ({ id }) => id
          );
          commit('set_requestload_invoices', allLoadsData);
          const previous =
            rootGetters['stripe/get_transaction_invoices_data'] || [];
          const allData = uniqBy(
            [...transactions[1], ...previous],
            ({ id }) => id
          );
          commit('set_transaction_invoices', allData);
        });

      const customerData = (await customerRef.get()).data();
      let agencyCustomerId = null;
      const customerId = customerData?.customer_id;
      if (customerData?.agency_customer_id) {
        agencyCustomerId = customerData?.agency_customer_id;
      }

      // Getting transactions invoices to the agent requested using agency payment method
      if (agencyCustomerId) {
        const { docs } = await firebase
          .firestore()
          .collection('stripe_customers')
          .where('customer_id', '==', agencyCustomerId)
          .get();
        const agencyCustomerRef = docs[0].ref;
        await agencyCustomerRef
          .collection('invoices')
          .where('metadata.createdBy', '==', customerId)
          .onSnapshot(({ docs }) => {
            const paymentData = docs.map((val) => val.data());
            const transactionsData = partition(paymentData, function(a) {
              return a.description?.includes('committed');
            });
            const previousLoads =
              rootGetters['stripe/get_requestload_invoices'] || [];
            const allLoadsData = uniqBy(
              [...transactionsData[0], ...previousLoads],
              ({ id }) => id
            );
            commit('set_requestload_invoices', allLoadsData);
            const previous =
              rootGetters['stripe/get_transaction_invoices_data'] || [];
            const allData = uniqBy(
              [...transactionsData[1], ...previous],
              ({ id }) => id
            );

            commit('set_transaction_invoices', allData);
          });
      }
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
  // Getting pending invoice details by id from stripe
  get_pending_invoice_by_id: async (
    { commit, dispatch, rootGetters },
    payload
  ) => {
    try {
      if (payload.length && payload.find((val) => val?.status !== 'paid')) {
        const { data } = await axios.get(
          `${process.env.VUE_APP_CANONICAL_URL}/api/v1/stripe/invoices/getOpenInvoices?customerId=${rootGetters['auth/get_customer'].customer_id}`
        );
        const pendingTransactions = data?.filter(
          (val) =>
            val.billing_reason === 'manual' &&
            val.status !== 'paid' &&
            !val.description?.includes('committed')
        );
        commit('set_pending_invoice', pendingTransactions);
        return data;
      } else {
        commit('set_pending_invoice', []);
      }
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
  update_shipper_locations: async ({ rootGetters, dispatch }, payload) => {
    try {
      await firebase
        .firestore()
        .collection('stripe_customers')
        .doc(`${rootGetters['auth/get_org'].owner_uid}`)
        .update({ shipper_locations: payload });
    } catch ({ message }) {
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          error: true,
          message,
        },
        { root: true }
      );
    }
  },
};

const getters = {
  get_payment_methods(state) {
    return state.payment_methods;
  },
  get_payout_methods(state) {
    return state.payout_methods;
  },
  get_payment_methods_status(state) {
    return state.payment_method_status;
  },
  get_payments(state) {
    return state.payments
      .map((payment) => {
        const {
          amount,
          created,
          metadata: { turn_id: turnId },
        } = payment;
        const formattedAmount = formatCurrency(amount * 0.01);

        return {
          ...payment,
          date: moment.unix(created),
          turn_id: turnId,
          amount: formattedAmount,
        };
      })
      .sort((a, b) => getTimeDiff(a.date, b.date))
      .reverse();
  },
  get_payouts(state) {
    return state.payouts;
  },
  get_subscription(state) {
    return state.subscription;
  },
  get_product_plans(state) {
    return state.product_plans;
  },
  get_invoice_data(state) {
    return state.invoice_data;
  },
  get_subscription_invoices_data(state) {
    return state.subscription_invoices;
  },
  get_transaction_invoices_data(state) {
    return state?.transaction_invoices;
  },
  get_pending_invoice_data(state) {
    return state?.pending_inovice_data;
  },
  get_requestload_invoices(state) {
    return state?.requestload_invoices;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
